<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { ArrowLeft, CircleAlert, LoaderCircle } from 'lucide-vue-next';

const props = withDefaults(defineProps<{
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'warning';
  busy?: boolean;
  error?: string;
}>(), {
  confirmLabel: 'Sí, salir',
  cancelLabel: 'Continuar aquí',
  tone: 'default',
  busy: false,
  error: '',
});

const emit = defineEmits<{
  cancel: [];
  confirm: [];
}>();

const cancelButton = ref<HTMLButtonElement>();

watch(() => props.open, async (open) => {
  if (!open) return;
  await nextTick();
  cancelButton.value?.focus();
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="webapp-confirmation" :class="{ 'webapp-confirmation--warning': tone === 'warning' }" @click.self="!busy && emit('cancel')" @keydown.esc="!busy && emit('cancel')">
      <section :role="tone === 'warning' ? 'alertdialog' : 'dialog'" aria-modal="true" aria-labelledby="webapp-confirmation-title" aria-describedby="webapp-confirmation-description" :aria-busy="busy">
        <span class="webapp-confirmation__icon" aria-hidden="true"><CircleAlert v-if="tone === 'warning'" :size="25" /><ArrowLeft v-else :size="23" /></span>
        <h2 id="webapp-confirmation-title">{{ title }}</h2>
        <p id="webapp-confirmation-description">{{ description }}</p>
        <p v-if="error" class="webapp-confirmation__error" role="alert">{{ error }}</p>
        <div class="webapp-confirmation__actions">
          <button ref="cancelButton" type="button" :disabled="busy" @click="emit('cancel')">{{ cancelLabel }}</button>
          <button class="webapp-confirmation__confirm" type="button" :disabled="busy" @click="emit('confirm')"><LoaderCircle v-if="busy" :size="17" class="webapp-confirmation__spinner" />{{ confirmLabel }}</button>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.webapp-confirmation {
  position: fixed;
  inset: 0;
  z-index: 2200;
  padding:
    calc(20px + env(safe-area-inset-top, 0px))
    calc(20px + env(safe-area-inset-right, 0px))
    calc(20px + env(safe-area-inset-bottom, 0px))
    calc(20px + env(safe-area-inset-left, 0px));
  display: grid;
  place-items: center;
  background: rgba(8, 12, 25, .52);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
}
.webapp-confirmation section {
  width: min(390px, 100%);
  padding: 25px;
  border-radius: 20px;
  color: #17202a;
  background: #fff;
  box-shadow: 0 24px 80px rgba(5, 9, 20, .28);
}
.webapp-confirmation__icon {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  color: #3f621c;
  background: #e6f6c9;
}
.webapp-confirmation h2 { margin: 18px 0 0; font-family: Georgia, "Times New Roman", serif; font-size: 25px; font-weight: 500; letter-spacing: -.5px; }
.webapp-confirmation p { margin: 10px 0 22px; color: #697185; font-size: 12px; line-height: 1.6; }
.webapp-confirmation__actions { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.webapp-confirmation__actions button { min-height: 46px; padding: 0 13px; border: 1px solid #dfe2e7; border-radius: 11px; color: #4d5668; background: #fff; font-size: var(--webapp-action-font-size); font-weight: 800; cursor: pointer; -webkit-tap-highlight-color: transparent; }
.webapp-confirmation__actions button:focus-visible { outline: 2px solid #8dbb3c; outline-offset: 2px; }
.webapp-confirmation__actions .webapp-confirmation__confirm { color: #fff; border-color: #182038; background: #182038; }
.webapp-confirmation__actions button:disabled { cursor: wait; opacity: .6; }
.webapp-confirmation__actions .webapp-confirmation__confirm { display: flex; align-items: center; justify-content: center; gap: 7px; }
.webapp-confirmation--warning section { border: 1px solid #fdba74; background: #fff7ed; }
.webapp-confirmation--warning .webapp-confirmation__icon { color: #c2410c; background: #ffedd5; }
.webapp-confirmation--warning h2 { color: #9a3412; }
.webapp-confirmation--warning p { color: #9a4c25; }
.webapp-confirmation--warning .webapp-confirmation__confirm { color: #fff; border-color: #c43d49; background: #c43d49; }
.webapp-confirmation--warning .webapp-confirmation__confirm:hover:not(:disabled) { border-color: #a72c38; background: #a72c38; }
.webapp-confirmation .webapp-confirmation__error { margin-top: 0; color: #b33c48; }
.webapp-confirmation__spinner { animation: webapp-confirmation-spin .8s linear infinite; }
@keyframes webapp-confirmation-spin { to { transform: rotate(360deg); } }

@media (max-width: 380px) {
  .webapp-confirmation section { padding: 21px; }
  .webapp-confirmation__actions { grid-template-columns: 1fr; }
}
</style>
